/**
 * AppointmentCountsReportTab — how many appointments per day / week / month.
 *
 * Sister to RevenueReportTab. Uses the same chart primitives (recharts
 * stacked bar) and the same visual language, but the number in every
 * cell is a count instead of AUD.
 *
 * Backend contract: GET /api/reports/appointment-counts?granularity=…&dimension=…
 * See routes/appointments_count_report.py.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { RefreshCw, CalendarClock, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getCached, setCached } from "@/lib/pageStateCache";

const SERIES_COLORS = [
  "#18181b", "#10b981", "#3b82f6", "#f59e0b",
  "#a855f7", "#ec4899", "#06b6d4", "#84cc16", "#94a3b8",
];

const GRANULARITY_OPTIONS = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];

const N_OPTIONS = {
  day:   [{ v: 7, l: "Last 7 days" }, { v: 14, l: "Last 14 days" }, { v: 30, l: "Last 30 days" }, { v: 60, l: "Last 60 days" }, { v: 90, l: "Last 90 days" }],
  week:  [{ v: 4, l: "Last 4 weeks" }, { v: 8, l: "Last 8 weeks" }, { v: 12, l: "Last 12 weeks" }, { v: 26, l: "Last 26 weeks" }, { v: 52, l: "Last 52 weeks" }],
  month: [{ v: 3, l: "Last 3 months" }, { v: 6, l: "Last 6 months" }, { v: 12, l: "Last 12 months" }, { v: 24, l: "Last 24 months" }],
};

const DIMENSION_OPTIONS = [
  { value: "total", label: "Total only" },
  { value: "calendar", label: "By calendar (practitioner)" },
  { value: "appointment_type", label: "By appointment type" },
  { value: "status", label: "By status (booked / canceled / no-show)" },
];

function csvSafe(v) {
  const s = v == null ? "" : String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Small badge summarising the current-vs-previous delta so the
 * clinician can eyeball "are we busier or quieter than last time?"
 * without reading the tooltip.
 */
function PreviousDeltaBadge({ current, previous }) {
  if (!previous && !current) return null;
  const delta = current - previous;
  const pct = previous > 0 ? Math.round((delta / previous) * 100) : null;
  const up = delta > 0;
  const flat = delta === 0;
  const cls = flat
    ? "border-zinc-200 bg-zinc-50 text-zinc-700"
    : up
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : "border-rose-200 bg-rose-50 text-rose-900";
  const arrow = flat ? "→" : up ? "↑" : "↓";
  const pctLabel = pct === null ? "n/a" : `${up ? "+" : ""}${pct}%`;
  return (
    <span
      className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}
      data-testid="appt-count-delta-badge"
      title={`Previous period: ${previous} appointments; delta ${up ? "+" : ""}${delta}`}
    >
      {arrow} {up ? "+" : ""}{delta} vs prev · {pctLabel}
    </span>
  );
}

export default function AppointmentCountsReportTab() {
  const cached = getCached("reports-appt-counts");

  const [granularity, setGranularity] = useState(cached?.granularity ?? "week");
  const [dimension, setDimension] = useState(cached?.dimension ?? "total");
  const [nDays, setNDays] = useState(cached?.nDays ?? 30);
  const [nWeeks, setNWeeks] = useState(cached?.nWeeks ?? 12);
  const [nMonths, setNMonths] = useState(cached?.nMonths ?? 12);
  const [includeEarlyCancels, setIncludeEarlyCancels] = useState(
    cached?.includeEarlyCancels ?? false,
  );
  const [includeLateAndNoShow, setIncludeLateAndNoShow] = useState(
    cached?.includeLateAndNoShow ?? true,
  );
  const [comparePrevious, setComparePrevious] = useState(
    cached?.comparePrevious ?? false,
  );

  const [data, setData] = useState(cached?.data ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setCached("reports-appt-counts", {
      granularity, dimension, nDays, nWeeks, nMonths,
      includeEarlyCancels, includeLateAndNoShow, comparePrevious, data,
      ts: Date.now(),
    });
  }, [granularity, dimension, nDays, nWeeks, nMonths, includeEarlyCancels, includeLateAndNoShow, comparePrevious, data]);

  async function runReport() {
    setLoading(true);
    setError(null);
    try {
      const params = {
        granularity,
        dimension,
        include_early_cancels: includeEarlyCancels,
        include_late_cancels_and_noshows: includeLateAndNoShow,
      };
      if (granularity === "day") params.n_days = nDays;
      if (granularity === "week") params.n_weeks = nWeeks;
      if (granularity === "month") params.n_months = nMonths;
      if (comparePrevious) params.compare_previous = true;
      const r = await api.get("/reports/appointment-counts", { params, timeout: 180000 });
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Appointment counts failed", { description: msg });
    } finally {
      setLoading(false);
    }
  }

  const chartRows = useMemo(() => {
    if (!data?.chart) return [];
    const { x_axis: xa, series } = data.chart;
    const prevSeries = data?.previous?.chart?.series || [];
    return xa.map((label, i) => {
      const row = { label };
      for (const s of series) row[s.name] = s.data[i] || 0;
      // Add previous-period totals at the same X position (folded to
      // a single "Previous total" series so multi-dimension charts
      // don't blow up the legend with N × 2 bars).
      if (data?.previous) {
        row["Previous total"] = prevSeries.reduce(
          (a, s) => a + (Number(s.data?.[i]) || 0), 0,
        );
      }
      return row;
    });
  }, [data]);

  const totalCount = data?.chart?.grand_total || 0;

  function exportCsv() {
    if (!data?.chart) return;
    const xa = data.chart.x_axis || [];
    const series = data.chart.series || [];
    const lines = [];
    if (dimension === "total") {
      lines.push(["Period", "Appointments"].join(","));
      xa.forEach((label, i) => {
        lines.push([csvSafe(label), series[0]?.data?.[i] ?? 0].join(","));
      });
      lines.push(["Grand total", totalCount].join(","));
    } else {
      lines.push(["Dimension", ...xa, "Total"].map(csvSafe).join(","));
      series.forEach((s) => {
        const total = (s.data || []).reduce((a, b) => a + (Number(b) || 0), 0);
        lines.push([csvSafe(s.name), ...(s.data || []), total].join(","));
      });
      lines.push(["Grand total", ...xa.map((_, i) => {
        return series.reduce((a, s) => a + (s.data?.[i] || 0), 0);
      }), totalCount].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `appointment-counts-${granularity}-${dimension}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const nOptions = N_OPTIONS[granularity] || [];
  const nValue = granularity === "day" ? nDays : granularity === "week" ? nWeeks : nMonths;
  const setN = granularity === "day" ? setNDays : granularity === "week" ? setNWeeks : setNMonths;

  return (
    <div className="space-y-6" data-testid="appointment-counts-report">
      <Card className="p-4 border-zinc-200 shadow-none">
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Granularity</Label>
            <Select value={granularity} onValueChange={setGranularity}>
              <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="appt-count-granularity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GRANULARITY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Window</Label>
            <Select value={String(nValue)} onValueChange={(v) => setN(Number(v))}>
              <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="appt-count-window">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nOptions.map((o) => (
                  <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Break down by</Label>
            <Select value={dimension} onValueChange={setDimension}>
              <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="appt-count-dimension">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIMENSION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <div className="flex flex-col gap-1">
              <label
                className="inline-flex items-center gap-2 text-xs text-zinc-700 cursor-pointer h-5"
                data-testid="appt-count-include-late-noshow-label"
                title="Late cancels (LCXL fee applied) and no-shows — the ones that cost time or trigger a fee."
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-indigo-600"
                  checked={includeLateAndNoShow}
                  onChange={(e) => setIncludeLateAndNoShow(e.target.checked)}
                  data-testid="appt-count-include-late-noshow"
                />
                Include late cancels &amp; no-shows
              </label>
              <label
                className="inline-flex items-center gap-2 text-xs text-zinc-700 cursor-pointer h-5"
                data-testid="appt-count-include-early-cancels-label"
                title="Client canceled outside the LCXL window — no fee, no impact on the calendar for the day."
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-indigo-600"
                  checked={includeEarlyCancels}
                  onChange={(e) => setIncludeEarlyCancels(e.target.checked)}
                  data-testid="appt-count-include-early-cancels"
                />
                Include early cancels (no fee)
              </label>
              <label
                className="inline-flex items-center gap-2 text-xs text-zinc-700 cursor-pointer h-5"
                data-testid="appt-count-compare-previous-label"
                title="Overlay the same-length window immediately before the current one so you can see if this stretch is busier or quieter than last time."
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-indigo-600"
                  checked={comparePrevious}
                  onChange={(e) => setComparePrevious(e.target.checked)}
                  data-testid="appt-count-compare-previous"
                />
                Compare to previous period
              </label>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button onClick={runReport} disabled={loading} data-testid="appt-count-run">
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
            {loading ? "Loading…" : "Run report"}
          </Button>
          {data && (
            <Button variant="outline" onClick={exportCsv} data-testid="appt-count-export">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
          )}
        </div>

        {error && (
          <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-900" data-testid="appt-count-error">
            <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
            {error}
          </div>
        )}
      </Card>

      {data?.warnings?.length > 0 && (
        <Card className="p-3 border-amber-200 bg-amber-50 shadow-none">
          <div className="flex items-start gap-2 text-xs text-amber-900">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
            <div>
              {data.warnings.map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {data?.chart && (
        <Card className="p-4 border-zinc-200 shadow-none">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <CalendarClock className="h-4 w-4 text-indigo-700" />
            <div className="text-sm font-semibold">
              {totalCount} appointment{totalCount === 1 ? "" : "s"} between{" "}
              {data.window?.start} and {data.window?.end}
            </div>
            {data.previous && (
              <PreviousDeltaBadge current={totalCount} previous={data.previous.chart?.grand_total || 0} />
            )}
          </div>
          <div style={{ width: "100%", height: 320 }} data-testid="appt-count-chart">
            <ResponsiveContainer>
              <BarChart data={chartRows} margin={{ left: 4, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis
                  dataKey="label" tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  angle={granularity === "day" && chartRows.length > 14 ? -35 : 0}
                  textAnchor={granularity === "day" && chartRows.length > 14 ? "end" : "middle"}
                  height={granularity === "day" && chartRows.length > 14 ? 50 : 24}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  formatter={(v) => v}
                  labelStyle={{ color: "#18181b" }}
                  contentStyle={{ borderRadius: 6, border: "1px solid #e4e4e7" }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {(data.chart.series || []).map((s, i) => (
                  <Bar
                    key={s.name}
                    dataKey={s.name}
                    stackId="appts"
                    fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                  />
                ))}
                {data.previous && (
                  <Bar
                    dataKey="Previous total"
                    stackId="prev"
                    fill="#cbd5e1"
                    fillOpacity={0.75}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
          {data.previous && (
            <div className="mt-2 text-2xs text-zinc-500" data-testid="appt-count-previous-window">
              Comparing against {data.previous.window?.start} → {data.previous.window?.end}{" "}
              ({data.previous.chart?.grand_total || 0} appointments).
            </div>
          )}
        </Card>
      )}

      {data?.chart && (
        <Card className="p-0 border-zinc-200 shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="text-2xs uppercase tracking-wider">
                    {dimension === "total" ? "Period" : "Group"}
                  </TableHead>
                  {dimension === "total" ? (
                    <TableHead className="text-right text-2xs uppercase tracking-wider">Appointments</TableHead>
                  ) : (
                    <>
                      {(data.chart.x_axis || []).map((x) => (
                        <TableHead key={x} className="text-right text-2xs uppercase tracking-wider">{x}</TableHead>
                      ))}
                      <TableHead className="text-right text-2xs uppercase tracking-wider">Total</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {dimension === "total" ? (
                  <>
                    {(data.chart.x_axis || []).map((label, i) => (
                      <TableRow key={label}>
                        <TableCell className="text-sm">{label}</TableCell>
                        <TableCell className="text-right font-mono-num text-sm">
                          {data.chart.series?.[0]?.data?.[i] ?? 0}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-semibold bg-zinc-50">
                      <TableCell className="text-sm">Grand total</TableCell>
                      <TableCell className="text-right font-mono-num text-sm">{totalCount}</TableCell>
                    </TableRow>
                  </>
                ) : (
                  <>
                    {(data.chart.series || []).map((s) => {
                      const rowTotal = (s.data || []).reduce((a, b) => a + (Number(b) || 0), 0);
                      return (
                        <TableRow key={s.name}>
                          <TableCell className="text-sm">{s.name}</TableCell>
                          {(s.data || []).map((v, i) => (
                            <TableCell key={i} className="text-right font-mono-num text-sm">{v}</TableCell>
                          ))}
                          <TableCell className="text-right font-mono-num text-sm font-semibold">{rowTotal}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="font-semibold bg-zinc-50">
                      <TableCell className="text-sm">Grand total</TableCell>
                      {(data.chart.x_axis || []).map((_, i) => (
                        <TableCell key={i} className="text-right font-mono-num text-sm">
                          {(data.chart.series || []).reduce((a, s) => a + (s.data?.[i] || 0), 0)}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-mono-num text-sm">{totalCount}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
